import React, { useState, useEffect } from 'react';

import { useNotification } from 'config/Notification';
import {
    fetchRHYMSCANDimensions as fetchRHYMSCANDimensionsService,
    fetchRHYMSCANSubDimensionsByDimensionId as fetchRHYMSCANSubDimensionsByDimensionIdService,
    updateDimensionweightages as updateDimensionweightagesService,
} from 'services/modules/rhymscan/data';

import Breadcrumbs from 'config/Breadcrumb';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
    Card,
    CardContent,
    Divider,
    Stepper,
    Step,
    StepLabel,
    Button,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    TextField,
    Grid,
    Typography,
    Box,
    CircularProgress,
    CardHeader,
} from '@mui/material';


const DimensionWeightageConfigure = () => {

    // State Variables
    const [selectedDimensions, setSelectedDimensions] = useState([]); 
    const [subDimension, setSubDimension] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(true); 
    const [selectedDimension, setSelectedDimension] = useState(''); 

    // Notification
    const { openNotification } = useNotification();

    // Method handles to fetch all dimensions
    const fetchAllDimensions = async () => {
        try {
            const response = await fetchRHYMSCANDimensionsService();
            if (response.data) {
                setSelectedDimensions(response.data);
            } else {
                openNotification("No dimensions found", 'error');
            }
        } catch (error) {
            openNotification("Error while fetching data", 'error');
        } finally {
            setLoading(false); 
        }
    };

    // Method handles to fetch sub dimensions based on dimension 
    const fetchSubDimensionsByDimensionId = async (dimensionId) => {
        try {
            const response = await fetchRHYMSCANSubDimensionsByDimensionIdService({ dimensionId });
            setSubDimension(response.data || []);
        } catch (error) {
            openNotification("Error while fetching sub-dimensions", 'error');
        }
    };

    useEffect(() => {
        fetchAllDimensions();
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Method to validate the total weightage
    const validateTotalWeightage = (weightages) => {
        const total = weightages.reduce((acc, curr) => acc + parseFloat(curr.weight), 0);
       // Round the total to avoid floating-point precision issues
    const roundedTotal = Math.round(total * 100) / 100;

    if (roundedTotal < 10) {
        return "Total weightage must be exactly 10, should not be less than 10.";
    } else if (roundedTotal > 10) {
        return "Total weightage must be exactly 10, should not be greater than 10.";
    } else {
        return null;
    }
    };
    
    // Validation schema for dimension weighatge 
    const validationSchemaStep1 = Yup.object({
        weightages: Yup.array()
            .of(
                Yup.object().shape({
                    id: Yup.string().required('Required'),
                    weight: Yup.number()
                        .required('Required')
                        .min(0, 'Min value is 0')
                        .max(10, 'Max value is 10')
                        .test(
                            'is-decimal',
                            'Weight must be a valid decimal (e.g., 0.2, 1.2, 1.55)',
                            value => /^\d+(\.\d{1,2})?$/.test(value)
                        ),
                })
            )
    });
    
    // Validation schema for sub dimension weightage
    const validationSchemaStep2 = Yup.object({
        dimension: Yup.string().required('Dimension is required'),
        weightages: Yup.array()
            .of(
                Yup.object().shape({
                    id: Yup.string().required('Required'),
                    weight: Yup.number()
                        .required('Required')
                        .min(0, 'Min value is 0')
                        .max(10, 'Max value is 10')
                        .test(
                            'is-decimal',
                            'Weight must be a valid decimal (e.g., 0.2, 1.2, 1.55)',
                            value => /^\d+(\.\d{1,2})?$/.test(value)
                        ),
                })
            ),
    });

    // Formik setup for dimension weightage
    const formikStep1 = useFormik({
        initialValues: {
            weightages: selectedDimensions.map((dimension) => ({
                label: dimension.label,
                slug: dimension.slug,
                id: dimension.id,
                weight: dimension.weight || 0,
            })),
        },
        validationSchema: validationSchemaStep1,
        onSubmit: async (values) => {
            const error = validateTotalWeightage(values.weightages);
            if (error) {
                alert(error);
                return;
            }
            try {

               let bodylist=[];
                values.weightages.forEach((e)=>{
                    let obj = {
                        "id": e.id,
                        "weight": e.weight
                    }
                    bodylist.push(obj);
                })
                const response = await updateDimensionweightagesService({ body: bodylist });
                if (response.status === 200) {
                    openNotification("Dimensions weightages updated successfully", 'success');
                    fetchAllDimensions();
                }
                setActiveStep(1); 
            } catch (error) {
                openNotification("Error while updating dimensions", 'error');
            }
        },
        enableReinitialize: true, // Keep it true to update form state when dimensions change
    });

    // Formikmsetup for sub dimension weightage
    const formikStep2 = useFormik({
        initialValues: {
            dimension: selectedDimension, 
            weightages: subDimension.map((subDim) => ({
                label: subDim.label,
                slug: subDim.slug,
                id: subDim.id,
                weight: subDim.weight || 0,
            })),
        },
        validationSchema: validationSchemaStep2,
        onSubmit: async (values) => {
            const error = validateTotalWeightage(values.weightages);
            if (error) {
                alert(error);
                return;
            }
            try {
                let bodylist=[];
                values.weightages.forEach((e)=>{
                    let obj = {
                        "id": e.id,
                        "weight": e.weight
                    }
                    bodylist.push(obj);
                })
                const response = await updateDimensionweightagesService({ body:bodylist });
                if (response.status === 200) {
                    openNotification("Sub-Dimensions weightages updated successfully", 'success');
                    fetchSubDimensionsByDimensionId(formikStep2.values.dimension);
                }
            } catch (error) {
                openNotification("Error while updating sub-dimensions", 'error');
            }
        },
        enableReinitialize: true, // Allow reinitialization when sub-dimensions change
    });

    useEffect(() => {
        if (selectedDimension) {
            fetchSubDimensionsByDimensionId(selectedDimension);
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDimension]);

    // Method handles to change the selected dimension
    const handleDimensionChange = (event) => {
        const dimensionId = event.target.value;
        setSelectedDimension(dimensionId); 
        formikStep2.setFieldValue('dimension', dimensionId);
    };

    return (
        <div className="main-cont">
            <Card>
            <CardHeader
            title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                  <Breadcrumbs paths={['RHYMScan Configurator','Dimension Weightages']} />
                  </label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={() => window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
                <CardContent>
                    <Divider />
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {['Add Dimension Weightage', 'Add Sub-Dimension Weightage'].map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    <Divider />

                    {loading ? (
                        <Box display="flex" justifyContent="center" mt={5}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {activeStep === 0 && (
                                <CardContent>
                                    <form onSubmit={formikStep1.handleSubmit}>
                                    {formikStep1.values.weightages.map((weightage, index) => (
                                        <Box key={weightage.id} mb={2}>
                                            {/* Group two weightages into a single row */}
                                            {index % 2 === 0 && (
                                            <Grid container spacing={2} alignItems="center">
                                                {/* First label and weight */}
                                                <Grid item xs={6}>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={6}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        {formikStep1.values.weightages[index]?.label}
                                                    </Typography>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                    <TextField
                                                        label="Weight"
                                                        name={`weightages[${index}].weight`}
                                                        value={formikStep1.values.weightages[index]?.weight}
                                                        onChange={formikStep1.handleChange}
                                                        error={
                                                        formikStep1.touched.weightages?.[index]?.weight &&
                                                        Boolean(formikStep1.errors.weightages?.[index]?.weight)
                                                        }
                                                        helperText={
                                                        formikStep1.touched.weightages?.[index]?.weight &&
                                                        formikStep1.errors.weightages?.[index]?.weight
                                                        }
                                                        fullWidth
                                                        margin="normal"
                                                        inputProps={{ step: '0.1' }}
                                                    />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        </Grid>
                                                </Grid>
                                                </Grid>

                                                {/* Second label and weight (if it exists) */}
                                                {formikStep1.values.weightages[index + 1] && (
                                                <Grid item xs={6}>
                                                    <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={2}>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="body1" color="textSecondary">
                                                        {formikStep1.values.weightages[index + 1]?.label}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <TextField
                                                        label="Weight"
                                                        name={`weightages[${index + 1}].weight`}
                                                        value={formikStep1.values.weightages[index + 1]?.weight}
                                                        onChange={formikStep1.handleChange}
                                                        error={
                                                            formikStep1.touched.weightages?.[index + 1]?.weight &&
                                                            Boolean(formikStep1.errors.weightages?.[index + 1]?.weight)
                                                        }
                                                        helperText={
                                                            formikStep1.touched.weightages?.[index + 1]?.weight &&
                                                            formikStep1.errors.weightages?.[index + 1]?.weight
                                                        }
                                                        fullWidth
                                                        margin="normal"
                                                        inputProps={{ step: '0.1' }}
                                                        />
                                                    </Grid>
                                                    </Grid>
                                                </Grid>
                                                )}
                                            </Grid>
                                            )}
                                        </Box>
                                        ))}
                                        <Box display="flex" justifyContent="center" mt={3}>
                                            <Button color="primary" variant="contained" type="submit">
                                                Submit
                                            </Button>
                                            <Button color="primary"
                                            className='mx-3'
                                            variant="contained" onClick={() => setActiveStep(1)}>
                                                Next
                                            </Button>
                                        </Box>

                                    </form>
                                </CardContent>
                            )}

                            {activeStep === 1 && (
                                <CardContent>
                                    <form onSubmit={formikStep2.handleSubmit}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <FormControl fullWidth margin="normal">
                                                    <InputLabel>Dimension</InputLabel>
                                                    <Select
                                                        name="dimension"
                                                        value={formikStep2.values.dimension}
                                                        onChange={handleDimensionChange}
                                                        error={formikStep2.touched.dimension && Boolean(formikStep2.errors.dimension)}
                                                    >
                                                        {selectedDimensions.map((dimension) => (
                                                            <MenuItem key={dimension.id} value={dimension.id}>
                                                                {dimension.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        {formikStep2.values.weightages.map((weightage, index) => (
                                            <Box key={weightage.id} mb={2}>
                                                {/* Group two weightages into a single row */}
                                                {index % 2 === 0 && (
                                                <Grid container spacing={2} alignItems="center">
                                                    {/* First label and weight */}
                                                    <Grid item xs={6}>
                                                    <Grid container spacing={2} alignItems="center">
                                                        <Grid item xs={6}>
                                                        <Typography variant="body1" color="textSecondary">
                                                            {formikStep2.values.weightages[index]?.label}
                                                        </Typography>
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                        <TextField
                                                            label="Weight"
                                                            name={`weightages[${index}].weight`}
                                                            value={formikStep2.values.weightages[index]?.weight}
                                                            onChange={formikStep2.handleChange}
                                                            error={
                                                            formikStep2.touched.weightages?.[index]?.weight &&
                                                            Boolean(formikStep2.errors.weightages?.[index]?.weight)
                                                            }
                                                            helperText={
                                                            formikStep2.touched.weightages?.[index]?.weight &&
                                                            formikStep2.errors.weightages?.[index]?.weight
                                                            }
                                                            fullWidth
                                                            margin="normal"
                                                            inputProps={{ step: '0.1' }}
                                                        />
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                        </Grid>
                                                    </Grid>
                                                    </Grid>

                                                    {/* Second label and weight (if it exists) */}
                                                    {formikStep2.values.weightages[index + 1] && (
                                                   
                                                   <Grid item xs={6}>
                                                        <Grid container spacing={2} alignItems="center">
                                                        <Grid item xs={2}>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="body1" color="textSecondary">
                                                            {formikStep2.values.weightages[index + 1]?.label}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                            label="Weight"
                                                            name={`weightages[${index + 1}].weight`}
                                                            value={formikStep2.values.weightages[index + 1]?.weight}
                                                            onChange={formikStep2.handleChange}
                                                            error={
                                                                formikStep2.touched.weightages?.[index + 1]?.weight &&
                                                                Boolean(formikStep2.errors.weightages?.[index + 1]?.weight)
                                                            }
                                                            helperText={
                                                                formikStep2.touched.weightages?.[index + 1]?.weight &&
                                                                formikStep2.errors.weightages?.[index + 1]?.weight
                                                            }
                                                            fullWidth
                                                            margin="normal"
                                                            inputProps={{ step: '0.1' }}
                                                            />
                                                        </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    )}
                                                </Grid>
                                                )}
                                            </Box>
                                            ))}

                                        <Box display="flex" justifyContent="center" mt={3}>
                                            <Button color="primary" variant="contained" onClick={() => setActiveStep(0)}>
                                                Back
                                            </Button>
                                            <Button 
                                            className='mx-3'
                                            color="primary" variant="contained" type="submit">
                                                Submit
                                            </Button>
                                        </Box>
                                    </form>
                                </CardContent>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default DimensionWeightageConfigure;
